/**
 * UTM Parameter Capture Utility
 * Extracts and stores UTM parameters from URL for analytics
 */

import type { DynamicInboundParam } from '@playcastdotio/Messaging';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  // Ad tracking parameters
  ad_platform?: string; // Reddit, YouTube, Instagram, etc.
  ad_campaign_id?: string;
  ad_creative_id?: string;
}

/**
 * Captured dynamic inbound parameters with resolved values.
 * Keys are the param names, values are captured from URL or static fallbacks.
 */
export type CapturedDynamicParams = Record<string, string>;

/**
 * Extract UTM parameters and ad tracking parameters from current URL
 */
export function captureUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);

  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    // Ad tracking parameters
    ad_platform: params.get('ad_platform') || undefined,
    ad_campaign_id: params.get('ad_campaign_id') || undefined,
    ad_creative_id: params.get('ad_creative_id') || undefined,
  };
}

/**
 * Store UTM params in sessionStorage for persistence across navigation
 */
export function storeUTMParams(params: UTMParams): void {
  try {
    sessionStorage.setItem('quickstart_utm', JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to store UTM params:', error);
  }
}

/**
 * Retrieve stored UTM params from sessionStorage
 */
export function getStoredUTMParams(): UTMParams | null {
  try {
    const stored = sessionStorage.getItem('quickstart_utm');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to retrieve stored UTM params:', error);
    return null;
  }
}

/**
 * Get UTM params - from URL if present, otherwise from storage
 */
export function getUTMParams(): UTMParams {
  const urlParams = captureUTMParams();

  // If URL has UTM params, store them and return
  if (Object.values(urlParams).some(v => v !== undefined)) {
    storeUTMParams(urlParams);
    return urlParams;
  }

  // Otherwise return stored params or empty object
  return getStoredUTMParams() || {};
}

/**
 * Clear stored UTM params
 */
export function clearUTMParams(): void {
  try {
    sessionStorage.removeItem('quickstart_utm');
  } catch (error) {
    console.warn('Failed to clear UTM params:', error);
  }
}

// ============================================================================
// DYNAMIC INBOUND PARAMETER CAPTURE (per-slug URL config system)
// ============================================================================

const DYNAMIC_PARAMS_STORAGE_PREFIX = 'quickstart_dynamic_params';

function getDynamicParamsKey(slug?: string): string {
  return slug ? `${DYNAMIC_PARAMS_STORAGE_PREFIX}_${slug}` : DYNAMIC_PARAMS_STORAGE_PREFIX;
}

/**
 * Capture dynamic inbound parameters from the current URL.
 * For each configured param: reads from URL query, falls back to staticFallback.
 * Stores captured values in sessionStorage scoped by slug.
 */
export function captureDynamicParams(paramDefs: DynamicInboundParam[], slug?: string): CapturedDynamicParams {
  const urlParams = new URLSearchParams(window.location.search);
  const captured: CapturedDynamicParams = {};

  for (const def of paramDefs) {
    const urlValue = urlParams.get(def.key);
    if (urlValue) {
      captured[def.key] = urlValue;
    } else if (def.staticFallback) {
      captured[def.key] = def.staticFallback;
    }
  }

  // Also capture standard UTMs from URL for pass-through
  const standardUtms = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
  for (const key of standardUtms) {
    if (captured[key]) continue; // Already captured as dynamic param
    const value = urlParams.get(key);
    if (value) captured[key] = value;
  }

  if (Object.keys(captured).length > 0) {
    storeDynamicParams(captured, slug);
  }
  return captured;
}

/**
 * Store captured dynamic params in sessionStorage (scoped by slug)
 */
export function storeDynamicParams(params: CapturedDynamicParams, slug?: string): void {
  try {
    sessionStorage.setItem(getDynamicParamsKey(slug), JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to store dynamic params:', error);
  }
}

/**
 * Retrieve stored dynamic params from sessionStorage (scoped by slug)
 */
export function getStoredDynamicParams(slug?: string): CapturedDynamicParams {
  try {
    const stored = sessionStorage.getItem(getDynamicParamsKey(slug));
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to retrieve stored dynamic params:', error);
    return {};
  }
}

